import { FC, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { cn } from 'utils/helpers';
import Button from 'ui/components/common/Button';
import { NoRecordIcon, NotFoundIcon } from 'ui/components/icon';
import { CgSpinner } from 'react-icons/cg';

const ICONS = {
  'no-data': NoRecordIcon,
  error: NotFoundIcon,
  loading: CgSpinner,
};

// eslint-disable-next-line react-refresh/only-export-components
export const ICON_TYPES = Object.keys(ICONS);

type PagePlaceholderProps = {
  className?: string | string[];
  iconType?: (typeof ICON_TYPES)[number];
  title?: string;
  description?: string;
  backButtonText?: string;
  onClick?: () => void;
  hasButton?: boolean;
  iconClassname?: string;
};

const PagePlaceholder: FC<PagePlaceholderProps> = ({
  className,
  iconType = 'no-data',
  title = '404',
  description = 'We can’t find any record matching your search.',
  onClick,
  backButtonText = 'BACK TO DASHBOARD',
  hasButton = true,
}) => {
  const navigate = useNavigate();

  const icon = useMemo(() => {
    switch (iconType) {
      case 'no-data':
        return <NoRecordIcon className="text-content" />;

      case 'loading':
        return (
          <CgSpinner className="text-primary-base animate-spin text-[50px]" />
        );

      default:
        return <NotFoundIcon className="text-content" />;
    }
  }, [iconType]);

  const handleClickHome = () => (onClick ? onClick() : navigate('/'));

  return (
    <div className={cn(className, 'flex flex-col gap-y-[22px] items-center')}>
      <div
        className={cn(
          'flex items-center justify-center',
          'p-8 rounded-full bg-fill-primary',
          'h-[120px] w-[120px]',
        )}
      >
        {icon}
      </div>
      <div className="text-center">
        <h3 className="text-[22px] font-semibold">{title}</h3>
        <p>{description}</p>
      </div>
      {!hasButton ? null : (
        <Button
          onClick={handleClickHome}
          className="mx-auto"
          intent="primary"
          size="sm"
          label={backButtonText}
        />
      )}
    </div>
  );
};

export const Error: FC<PagePlaceholderProps> = (props) => (
  <PagePlaceholder {...props} iconType="error" />
);

export const NoData: FC<PagePlaceholderProps> = (
  props = {
    hasButton: true,
    iconClassname: '',
    className: '',
    iconType: ICON_TYPES[1],
    title: 'No record yet',
    description: 'There seems to be no records available to show.',
    backButtonText: 'BACK TO HOME',
    onClick: () => null,
  },
) => <PagePlaceholder {...props} iconType="no-data" />;

export const Loading: FC<PagePlaceholderProps> = (props) => (
  <PagePlaceholder
    iconType="loading"
    iconClassname="text-primary-base text-9xl animate-spin"
    title="Loading"
    hasButton={false}
    {...props}
  />
);

export default PagePlaceholder;
